import { useEffect } from 'react';

import { useAuthMe } from '@/api/generated/endpoints/auth/auth';
import type { MeOut } from '@/api/generated/model';
import { ApiProblem } from '@/api/http';

export interface CurrentUser {
  user: MeOut | null;
  isLoading: boolean;
  /** Ошибка, отличная от «не выполнен вход» (сервер недоступен и т.п.). */
  error: ApiProblem | null;
}

const ME_KEY = 'sww.me.v1';

function cachedMe(): MeOut | null {
  try {
    const raw = window.localStorage.getItem(ME_KEY);
    return raw ? (JSON.parse(raw) as MeOut) : null;
  } catch {
    return null;
  }
}

/** Запомнить вошедшего: без связи приложение откроется под ним, очередь продолжит копиться. */
export function rememberMe(me: MeOut | null): void {
  try {
    if (me) window.localStorage.setItem(ME_KEY, JSON.stringify(me));
    else window.localStorage.removeItem(ME_KEY);
  } catch {
    // хранилище недоступно — просто не запомним
  }
}

export function useCurrentUser(): CurrentUser {
  const { data, isPending, error } = useAuthMe({
    query: {
      retry: false,
      staleTime: Infinity,
      gcTime: Infinity,
      // Без связи — не перезапрашивать при каждом открытии компонента: в v5 запрос без данных
      // при перезапросе снова «загружается», и приложение мигало бы спиннером по кругу.
      // Связь вернулась — перезапрашивает OutboxProvider.
      retryOnMount: false,
    },
  });
  useEffect(() => {
    if (data) rememberMe(data);
  }, [data]);
  const unauthenticated = error instanceof ApiProblem && error.status === 401;
  // Сервер недоступен (не «не вошёл»): работаем под последним вошедшим — офлайн-режим.
  const offlineUser = error instanceof ApiProblem && error.status === 0 ? cachedMe() : null;
  return {
    user: data ?? offlineUser,
    isLoading: isPending && !error,
    error: error instanceof ApiProblem && !unauthenticated && !offlineUser ? error : null,
  };
}
